import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CircleAlert,
  ConciergeBell,
  GraduationCap,
  Mail,
  MailCheck,
  ShieldCheck,
  Store,
  Volleyball,
  WandSparkles,
  type LucideIcon,
} from 'lucide-react';
import { useLoginForm } from 'modules/jogador/auth/useLoginForm';
import { AuthLayout } from 'modules/jogador/auth/components/AuthLayout';
import { PasswordInput } from 'modules/jogador/auth/components/PasswordInput';
import { OrDivider, SocialButtons } from 'modules/jogador/auth/components/SocialButtons';
import { Input } from 'shared/components/ui/Input';
import { Button } from 'shared/components/ui/Button';
import { useToast } from 'shared/components/ui/Toast';
import { useAuth } from 'shared/hooks/useAuth';
import { ARENA_OWNER_SIGNUP_AUTO, ROUTES } from 'shared/constants/routes';
import { UserRole } from 'shared/types/user';

// Atalhos do modo apresentação: entram direto como cada persona, sem digitar e-mail.
const QUICK_ACCESS: { role: UserRole; label: string; icon: LucideIcon; to: string }[] = [
  { role: 'jogador', label: 'Jogador', icon: Volleyball, to: ROUTES.HOME },
  { role: 'arena', label: 'Dono de arena', icon: Store, to: ROUTES.ARENA_DASHBOARD },
  { role: 'recepcao', label: 'Recepção', icon: ConciergeBell, to: ROUTES.RECEPCAO_RESERVATIONS },
  { role: 'instrutor', label: 'Instrutor', icon: GraduationCap, to: ROUTES.INSTRUCTOR_CLASSES },
  { role: 'admin', label: 'Admin', icon: ShieldCheck, to: ROUTES.ADMIN_DASHBOARD },
];

const PROVIDER_NAME = { google: 'Google', apple: 'Apple' } as const;

export function LoginPage() {
  const { email, setEmail, password, setPassword, error, isSubmitting, handleSubmit } = useLoginForm();
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [remember, setRemember] = useState(true);
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoverySent, setRecoverySent] = useState(false);

  function enterAs(role: UserRole, to: string) {
    impersonate(role);
    navigate(to, { replace: true });
  }

  function handleSocial(provider: 'google' | 'apple') {
    enterAs('jogador', ROUTES.HOME);
    toast({ title: `Conectado com ${PROVIDER_NAME[provider]}`, description: 'Login social simulado no modo apresentação.' });
  }

  function handleRecoverySubmit(event: FormEvent) {
    event.preventDefault();
    setRecoverySent(true);
  }

  function backToLogin() {
    setIsRecoveringPassword(false);
    setRecoverySent(false);
    setRecoveryEmail('');
  }

  if (isRecoveringPassword) {
    return (
      <AuthLayout audience="login">
        <button
          type="button"
          onClick={backToLogin}
          className="mb-6 flex items-center gap-1.5 text-sm font-medium text-ink-secondary hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Voltar para o login
        </button>

        {recoverySent ? (
          <div className="text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-soft text-success">
              <MailCheck className="h-7 w-7" aria-hidden="true" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-ink">Confira seu e-mail</h1>
            <p className="mt-2 text-sm text-ink-secondary">
              Se existir uma conta com <strong className="text-ink">{recoveryEmail}</strong>, enviamos um link para
              criar uma nova senha. Ele vale por 30 minutos.
            </p>
            <Button className="mt-6 w-full" onClick={backToLogin}>
              Voltar para o login
            </Button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-bold text-ink">Esqueceu a senha?</h1>
            <p className="mt-2 text-sm text-ink-secondary">
              Sem problema. Informe o e-mail da sua conta e enviamos um link para criar uma nova.
            </p>
            <form onSubmit={handleRecoverySubmit} className="mt-8 flex flex-col gap-4">
              <Input
                label="E-mail"
                name="recoveryEmail"
                type="email"
                icon={Mail}
                placeholder="voce@email.com"
                autoComplete="email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                required
              />
              <Button type="submit" className="w-full py-2.5">
                Enviar link de recuperação
              </Button>
            </form>
          </>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout audience="login">
      <h1 className="font-heading text-[28px] font-bold leading-tight text-ink">Entrar na sua conta</h1>
      <p className="mt-2 text-sm text-ink-secondary">Bem-vindo de volta. Que bom te ver por aqui.</p>

      <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
        <Input
          label="E-mail"
          name="email"
          type="email"
          icon={Mail}
          placeholder="voce@email.com"
          autoComplete="email"
          className="py-2.5"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <PasswordInput
          name="password"
          autoComplete="current-password"
          placeholder="Sua senha"
          className="py-2.5"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <div className="flex items-center justify-between text-sm">
          <label className="flex cursor-pointer items-center gap-2 text-ink-secondary">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            Lembrar de mim
          </label>
          <button
            type="button"
            onClick={() => setIsRecoveringPassword(true)}
            className="font-medium text-primary hover:underline"
          >
            Esqueci a senha
          </button>
        </div>

        {error && (
          <p role="alert" className="flex items-center gap-2 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            <CircleAlert className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}

        <Button type="submit" className="w-full py-3" disabled={isSubmitting}>
          {isSubmitting ? 'Entrando...' : 'Entrar'}
        </Button>
      </form>

      <div className="my-6">
        <OrDivider />
      </div>
      <SocialButtons onSelect={handleSocial} disabled={isSubmitting} />

      <p className="mt-8 text-center text-sm text-ink-secondary">
        Não tem conta?{' '}
        <Link to={ROUTES.SIGNUP} className="font-semibold text-primary hover:underline">
          Criar conta grátis
        </Link>
      </p>

      <section className="mt-10 border-t border-border/60 pt-6" aria-label="Modo demonstração">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">Modo demonstração</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {QUICK_ACCESS.map(({ role, label, icon: Icon, to }) => (
            <button
              key={role}
              type="button"
              onClick={() => enterAs(role, to)}
              className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-ink-secondary transition-colors hover:border-primary hover:text-primary-dark"
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
        <Link
          to={ARENA_OWNER_SIGNUP_AUTO}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border py-2.5 text-sm text-ink-secondary transition-colors hover:border-primary hover:text-primary-dark"
        >
          <WandSparkles className="h-4 w-4" aria-hidden="true" />
          <span>
            <span className="font-medium">Simular cadastro de dono de arena</span> do início ao painel
          </span>
        </Link>
      </section>
    </AuthLayout>
  );
}
